<template>
  <div class="linexo-mini-variant">
    <div v-if="showAdLabel === 'true'" class="linexo-mini-variant__ad-label">
      {{ $t('content.ad_label') }}
    </div>
    <div class="linexo-mini-variant__main">
      <MiniHeader title="Dienstrad-Leasing" />
      <div v-if="!!resultErrorMessage" class="linexo-mini-variant__error-message">
        <AErrorMessageBox :is-mini-variant="true" />
      </div>
      <MiniCalculation
        v-if="currentStep"
        :is-valid="isLoading"
        :savings="savings"
        :savings-percentage="savingsPercentage"
        :current-step="currentStep"
        :steps="steps"
        :is-form-schema-loading="formSchemaLoading"
        :price="price"
        :has-valid-result="isCalculationResultValid"
        :is-loading="isLoading"
      />
    </div>
    <AInfoModal />
  </div>
</template>
<script lang="ts" setup>
import MiniHeader from './mini-header/MiniHeader.vue'
import MiniCalculation from '@/variants/mini-variant/mini-calculation/MiniCalculation.vue'
import AInfoModal from '@/components/atoms/modal/A-InfoModal.vue'
import { useFormStore } from '@/stores/formStore.ts'
import { storeToRefs } from 'pinia'
import { useGlobalStore } from '@/stores/globalStore'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useCalculationStore } from '@/stores/calculationStore'
import { useDataAttributes } from '@/composables/dataAttributes'
import { useTracking } from '@/composables/tracking'
import AErrorMessageBox from '@/components/atoms/error-message-box/A-ErrorMessageBox.vue'
import { APP_ID } from '@/config/constants.config'

const { currentStep, steps, formSchemaLoading } = storeToRefs(useFormStore())
const { isLoading, hasApiError } = storeToRefs(useGlobalStore())
const { calculatedResults, errors: calculationErrors } = storeToRefs(useCalculationStore())

const price = computed(() => calculatedResults.value?.monthlyNetBurden ?? 0)

const savings = computed(() => calculatedResults.value?.absoluteSavings ?? 0)
const savingsPercentage = computed(() => calculatedResults.value?.relativeSavings ?? 0)

const { showAdLabel } = useDataAttributes()
const tracking = useTracking()

const isCalculationResultValid = computed(
  () =>
    !calculationErrors.value?.length &&
    calculatedResults.value.monthlyNetBurden !== null &&
    calculatedResults.value.absoluteSavings !== null
)

const resultErrorMessage = computed(() => {
  if (hasApiError.value) {
    return 'error.api_error_mini_view'
  }
  return ''
})

onMounted(() => {
  const isEmbeddedInIframe = (() => {
    try {
      return window.self !== window.top
    } catch {
      return true
    }
  })()

  // In iframe mode, parent page decides visibility and sends `iframe_visible` via postMessage.
  if (isEmbeddedInIframe) return

  const runViewTracking = () => {
    tracking.viewMini()
  }

  // If host element is not found, track the view immediately to avoid missing tracking.
  const hostElement = document.getElementById(APP_ID)
  if (!hostElement) {
    runViewTracking()
    return
  }

  // If IntersectionObserver is not supported, track the view immediately to avoid missing tracking in unsupported browsers
  if (!('IntersectionObserver' in window)) {
    runViewTracking()
    return
  }

  // Use IntersectionObserver to track when the mini variant enters the viewport.
  nextTick(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          runViewTracking()
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(hostElement)
  })
})
</script>
<style lang="scss" scoped>
@import './mini-variant.scss';
</style>
